import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  listRecipients, getRecipient, eraseContact,
  type AuthenticatedSession, type Contact, type RecipientEnvelope,
} from '../../api'
import { dateTime } from '../../format'
import { AdminPage } from './AdminPage'
import { ConfirmSheet } from '../../components/ConfirmSheet'

/**
 * People this organization has asked to act on a document, and the erasure a
 * customer asks for on one of their behalf.
 *
 * A recipient is a contact — belief, what this organization asserts about a
 * person, under its controller relationship. It is not the evidence. Erasing
 * one removes the name, the addresses, what they typed into boxes and the
 * signature image they drew; it does not remove the record that they were
 * asked to sign and did. That record is on the chain, and docsdone does not
 * unilaterally delete evidence.
 *
 * The sender's own contact, created by sending, is not listed. Sending is not
 * being asked.
 *
 * Saying that plainly on the confirmation matters: an administrator clicking
 * "erase" is usually answering a request from the person themselves, and needs
 * to know what they can honestly tell them.
 */
export function Recipients({ session }: { session: AuthenticatedSession }) {
  return (
    <AdminPage
      session={session}
      title="Recipients"
      intro="People your senders have asked to sign, approve, view, or receive a copy."
    >
      {(orgId) => <RecipientList key={orgId} orgId={orgId} />}
    </AdminPage>
  )
}

export function Recipient({ session }: { session: AuthenticatedSession }) {
  const { contactId = '' } = useParams()
  const [recipient, setRecipient] = useState<Contact | null>(null)
  const addresses = recipient?.identifiers.map((i) => i.value).join(', ') ?? ''
  return (
    <AdminPage
      session={session}
      title={recipient?.display_name ?? 'Recipient'}
      intro={recipient
        ? (addresses || 'No address on file.')
        : 'Envelopes this person was asked to act on.'}
    >
      {(orgId) => (
        <RecipientHistory
          key={`${orgId}:${contactId}`}
          orgId={orgId}
          contactId={contactId}
          onLoaded={setRecipient}
        />
      )}
    </AdminPage>
  )
}

function RecipientList({ orgId }: { orgId: string }) {
  const [contacts, setContacts] = useState<Contact[] | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [pending, setPending] = useState<Contact | null>(null)
  const [busy, setBusy] = useState(false)
  const [sheetError, setSheetError] = useState('')
  const [query, setQuery] = useState('')

  const load = useCallback(() => {
    listRecipients(orgId)
      .then((r) => setContacts(r.recipients ?? []))
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not read the recipients.'))
  }, [orgId])

  // Only the load. Resetting to "loading" on an organization change is done by
  // remounting — the caller keys this component on the organization id — which
  // is what keeps one organization's roster from flashing under another's
  // heading without a cascading render to undo it.
  useEffect(load, [load])

  async function erase() {
    if (!pending) return
    setBusy(true)
    setSheetError('')
    try {
      await eraseContact(orgId, pending.id)
      setNotice(`${pending.display_name} has been erased. What they signed is unchanged.`)
      setPending(null)
      load()
    } catch (e) {
      setSheetError(e instanceof Error ? e.message : 'Could not erase that recipient.')
    } finally {
      setBusy(false)
    }
  }

  if (contacts === null && !error) return <p className="muted">Loading…</p>

  const needle = query.trim().toLowerCase()
  const shown = (contacts ?? []).filter(
    (c) =>
      needle === '' ||
      c.display_name.toLowerCase().includes(needle) ||
      c.identifiers.some((i) => i.value.toLowerCase().includes(needle)),
  )

  return (
    <>
      {error && <div className="notice notice-bad" style={{ marginBottom: '.9rem' }}>{error}</div>}
      {notice && <div className="notice notice-good" style={{ marginBottom: '.9rem' }}>{notice}</div>}

      {contacts?.length === 0 && (
        <div className="card empty">
          <h2>No recipients yet</h2>
          <p className="muted">Anyone your senders ask to sign, approve, view, or receive a copy appears here.</p>
        </div>
      )}

      {!!contacts?.length && (
        <>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <label htmlFor="recipient-search">Find someone</label>
            <input
              id="recipient-search"
              type="search"
              placeholder="Name or address"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="card card-tight table-wrap">
            <table className="table table-rows">
              <thead>
                <tr>
                  <th>Recipient</th>
                  <th>Addresses</th>
                  <th>First seen</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link to={`/orgs/${orgId}/recipients/${c.id}`}>
                        <strong>{c.display_name}</strong>
                      </Link>
                    </td>
                    <td>
                      {c.identifiers.map((i) => (
                        <div key={i.value} className="small">
                          {i.value}
                          {/* Verified means control of the address was
                              demonstrated, not that a sender typed it
                              carefully. It is the difference between evidence
                              about a mailbox and a claim about one. */}
                          {i.verified_at ? (
                            <span className="tag" style={{ marginLeft: '.4rem' }}>
                              verified · {i.verified_by}
                            </span>
                          ) : (
                            <span className="faint"> · Entered by the sender</span>
                          )}
                        </div>
                      ))}
                    </td>
                    <td className="muted small">{dateTime(c.created_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn btn-secondary btn-small"
                        aria-label={`Erase ${c.display_name}`}
                        onClick={() => { setSheetError(''); setPending(c) }}
                      >
                        Erase
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {shown.length === 0 && (
            <p className="muted" style={{ marginTop: '.9rem' }}>
              Nobody matches “{query}”.
            </p>
          )}
        </>
      )}

      {pending && (
        <ConfirmSheet
          title={`Erase ${pending.display_name}?`}
          confirmLabel="Erase this recipient"
          confirmWord={pending.display_name}
          busy={busy}
          error={sheetError}
          onConfirm={() => void erase()}
          onClose={() => setPending(null)}
        >
          <p className="small muted">
            <strong>Removed:</strong> their name, every address you hold for them, anything they
            typed into a box, and the signature image they drew.
          </p>
          <p className="small muted">
            <strong>Kept:</strong> the record that they were asked to sign, and did: who was asked,
            when, how they were authenticated, and the fingerprint of what they signed.
          </p>
          <p className="small muted">This cannot be undone.</p>
        </ConfirmSheet>
      )}
    </>
  )
}

function RecipientHistory({
  orgId,
  contactId,
  onLoaded,
}: {
  orgId: string
  contactId: string
  onLoaded: (recipient: Contact) => void
}) {
  const [envelopes, setEnvelopes] = useState<RecipientEnvelope[] | null>(null)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    getRecipient(orgId, contactId)
      .then((r) => {
        onLoaded(r.recipient)
        setEnvelopes(r.envelopes ?? [])
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not read that recipient.'))
  }, [orgId, contactId, onLoaded])

  useEffect(load, [load])

  return (
    <>
      <p>
        <Link to={`/orgs/${orgId}/recipients`}>All recipients</Link>
      </p>
      {error && <div className="notice notice-bad">{error}</div>}
      {envelopes === null && !error && <p className="muted">Loading…</p>}
      {envelopes?.length === 0 && (
        <div className="card empty">
          <h2>No envelopes</h2>
          <p className="muted">Nothing here asks this person to act.</p>
        </div>
      )}
      {!!envelopes?.length && (
        <div className="card card-tight table-wrap">
          <table className="table table-rows">
            <thead>
              <tr>
                <th>Envelope</th>
                <th>Role</th>
                <th>Status</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {envelopes.map((e) => (
                <tr key={`${e.id}-${e.role}`}>
                  <td>
                    <Link to={`/envelopes/${e.id}`}><strong>{e.title}</strong></Link>
                  </td>
                  <td className="muted small">{roleLabel(e.role)}</td>
                  <td><span className={`badge badge-${e.status}`}>{statusLabel(e.status)}</span></td>
                  <td className="muted small">{dateTime(e.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

function roleLabel(role: string): string {
  switch (role) {
    case 'signer': return 'Signer'
    case 'approver': return 'Approver'
    case 'cc': return 'Receives a copy'
    case 'needs_to_view': return 'Needs to view'
    case 'witness': return 'Witness'
    default: return role
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'completed': return 'Signed'
    case 'sent': return 'Pending'
    case 'draft': return 'Draft'
    case 'declined': return 'Declined'
    case 'voided': return 'Voided'
    default: return status
  }
}
